/**
 * Traduções centralizadas PT-BR ↔ EN
 * 
 * A API trabalha em inglês (padrão internacional)
 * O frontend trabalha em português (UX para usuários brasileiros)
 * 
 * Este arquivo centraliza todas as traduções para manter consistência
 */

// ============================================================================
// CATEGORIAS DE TICKETS
// ============================================================================

export const TICKET_CATEGORIES_PT_TO_EN: Record<string, string> = {
  "Suporte Técnico": "support",
  "Dúvida": "question",
  "Problema": "bug",
  "Solicitação": "feature",
  "Incidente": "incident",
  "Mudança": "change_request",
  "Outro": "other"
};

export const TICKET_CATEGORIES_EN_TO_PT: Record<string, string> = {
  "support": "Suporte Técnico",
  "question": "Dúvida",
  "bug": "Problema",
  "feature": "Solicitação",
  "incident": "Incidente",
  "change_request": "Mudança",
  "other": "Outro"
};

// Lista de categorias para exibição em selects
export const TICKET_CATEGORIES_LIST = [
  { value: "Suporte Técnico", label: "Suporte Técnico" },
  { value: "Dúvida", label: "Dúvida" },
  { value: "Problema", label: "Problema" },
  { value: "Solicitação", label: "Solicitação" },
  { value: "Incidente", label: "Incidente" },
  { value: "Mudança", label: "Mudança" },
  { value: "Outro", label: "Outro" }
];

// ============================================================================
// PRIORIDADES DE TICKETS
// ============================================================================

export const TICKET_PRIORITIES_PT_TO_EN: Record<string, string> = {
  "Baixa": "low",
  "Média": "medium",
  "Alta": "high",
  "Urgente": "urgent"
};

export const TICKET_PRIORITIES_EN_TO_PT: Record<string, string> = {
  "low": "Baixa",
  "medium": "Média",
  "high": "Alta",
  "urgent": "Urgente"
};

// Lista de prioridades para exibição em selects
export const TICKET_PRIORITIES_LIST = [
  { value: "Baixa", label: "Baixa" },
  { value: "Média", label: "Média" },
  { value: "Alta", label: "Alta" },
  { value: "Urgente", label: "Urgente" }
];

// ============================================================================
// STATUS DE TICKETS
// ============================================================================

export const TICKET_STATUS_PT_TO_EN: Record<string, string> = {
  "Novo": "novo",
  "Aberto": "open",
  "Atribuído": "atribuido",
  "Em Análise": "em_analise",
  "Aguardando Cliente": "aguardando_cliente",
  "Em Atendimento": "em_atendimento",
  "Aguardando Validação": "aguardando_validacao",
  "Em Andamento": "in_progress",
  "Aguardando Interno": "waiting_internal",
  "Resolvido": "resolvido",
  "Fechado": "fechado",
  "Cancelado": "cancelled"
};

export const TICKET_STATUS_EN_TO_PT: Record<string, string> = {
  "novo": "Novo",
  "open": "Aberto",
  "atribuido": "Atribuído",
  "em_analise": "Em Análise",
  "aguardando_cliente": "Aguardando Cliente",
  "em_atendimento": "Em Atendimento",
  "aguardando_validacao": "Aguardando Validação",
  "in_progress": "Em Andamento",
  "waiting_client": "Aguardando Cliente",
  "waiting_client_info": "Aguardando Cliente",
  "waiting_internal": "Aguardando Interno",
  "resolvido": "Resolvido",
  "resolved": "Resolvido",
  "fechado": "Fechado",
  "closed": "Fechado",
  "cancelled": "Cancelado"
};

// Lista de status para exibição em selects
export const TICKET_STATUS_LIST = [
  { value: "Novo", label: "Novo" },
  { value: "Atribuído", label: "Atribuído" },
  { value: "Em Análise", label: "Em Análise" },
  { value: "Aguardando Cliente", label: "Aguardando Cliente" },
  { value: "Em Atendimento", label: "Em Atendimento" },
  { value: "Aguardando Validação", label: "Aguardando Validação" },
  { value: "Resolvido", label: "Resolvido" },
  { value: "Fechado", label: "Fechado" },
  { value: "Cancelado", label: "Cancelado" }
];

// ============================================================================
// SEVERIDADE DE TICKETS
// ============================================================================

export const TICKET_SEVERITY_PT_TO_EN: Record<string, string> = {
  "Baixa": "low",
  "Moderada": "moderate",
  "Alta": "high",
  "Crítica": "critical"
};

export const TICKET_SEVERITY_EN_TO_PT: Record<string, string> = {
  "low": "Baixa",
  "moderate": "Moderada",
  "high": "Alta",
  "critical": "Crítica"
};

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Converte dados de ticket da API (EN) para o formato do frontend (PT-BR)
 */
export function mapTicketFromAPI(apiTicket: any) {
  return {
    id: apiTicket.id,
    numero: apiTicket.ticket_number,
    titulo: apiTicket.subject,
    descricao: apiTicket.description || "",
    categoria: TICKET_CATEGORIES_EN_TO_PT[apiTicket.category] || apiTicket.category,
    prioridade: TICKET_PRIORITIES_EN_TO_PT[apiTicket.priority] || apiTicket.priority,
    severidade: TICKET_SEVERITY_EN_TO_PT[apiTicket.severity] || apiTicket.severity,
    status: TICKET_STATUS_EN_TO_PT[apiTicket.status] || apiTicket.status,
    data_criacao: apiTicket.created_at,
    data_atualizacao: apiTicket.updated_at,
    data_fechamento: apiTicket.closed_at,
    cliente_id: apiTicket.client_id,
    cliente_nome: apiTicket.company_name || apiTicket.trading_name,
    projeto_id: apiTicket.project_id,
    projeto_nome: apiTicket.project_name,
    criado_por: apiTicket.created_by,
    criado_por_nome: apiTicket.created_by_name,
    atribuido_para: apiTicket.assigned_to,
    atribuido_para_nome: apiTicket.assigned_to_name,
    sla_resposta: apiTicket.sla_response_due,
    sla_resolucao: apiTicket.sla_resolution_due,
    total_mensagens: apiTicket.message_count || 0
  };
}

/**
 * Converte dados de ticket do frontend (PT-BR) para o formato da API (EN)
 */
export function mapTicketToAPI(frontendTicket: any) {
  return {
    subject: frontendTicket.titulo,
    description: frontendTicket.descricao,
    category: TICKET_CATEGORIES_PT_TO_EN[frontendTicket.categoria] || "other",
    priority: TICKET_PRIORITIES_PT_TO_EN[frontendTicket.prioridade] || "medium",
    severity: frontendTicket.severidade ? TICKET_SEVERITY_PT_TO_EN[frontendTicket.severidade] : undefined,
    status: frontendTicket.status ? TICKET_STATUS_PT_TO_EN[frontendTicket.status] : undefined,
    client_id: frontendTicket.cliente_id,
    project_id: frontendTicket.projeto_id,
    assigned_to: frontendTicket.atribuido_para
  };
}

/**
 * Converte lista de tickets da API para o frontend
 */
export function mapTicketListFromAPI(apiTickets: any[]) {
  return apiTickets.map(mapTicketFromAPI);
}

// ============================================================================
// CORES E ESTILOS POR CATEGORIA
// ============================================================================

export const TICKET_CATEGORY_COLORS: Record<string, string> = {
  "Suporte Técnico": "bg-blue-100 text-blue-800 border-blue-200",
  "Dúvida": "bg-purple-100 text-purple-800 border-purple-200",
  "Problema": "bg-red-100 text-red-800 border-red-200",
  "Solicitação": "bg-green-100 text-green-800 border-green-200",
  "Incidente": "bg-orange-100 text-orange-800 border-orange-200",
  "Mudança": "bg-indigo-100 text-indigo-800 border-indigo-200",
  "Outro": "bg-gray-100 text-gray-800 border-gray-200"
};

// ============================================================================
// CORES E ESTILOS POR PRIORIDADE
// ============================================================================

export const TICKET_PRIORITY_COLORS: Record<string, string> = {
  "Baixa": "bg-green-100 text-green-800 border-green-200",
  "Média": "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Alta": "bg-orange-100 text-orange-800 border-orange-200",
  "Urgente": "bg-red-100 text-red-800 border-red-200"
};

// ============================================================================
// VARIANTES DE BADGE POR STATUS
// ============================================================================

export const TICKET_STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  "Novo": "destructive",
  "Aberto": "destructive",
  "Atribuído": "default",
  "Em Análise": "default",
  "Aguardando Cliente": "secondary",
  "Em Atendimento": "default",
  "Aguardando Validação": "secondary",
  "Em Andamento": "default",
  "Aguardando Interno": "secondary",
  "Resolvido": "outline",
  "Fechado": "outline",
  "Cancelado": "outline"
};
